import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { errorMessage } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import LanguageSwitcher from "../components/LanguageSwitcher";

export default function AuthPage({ mode = "login" }) {
  const isLogin = mode === "login";
  const { t } = useI18n();
  const { login, register, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [referralCode, setReferralCode] = useState(params.get("ref") || "");
  const [loading, setLoading] = useState(false);

  // Redirect if already logged in
  useEffect(() => {
    if (!authLoading && user) {
      const plan = params.get("plan");
      const billing = params.get("billing");
      if (plan && billing) navigate(`/app/account?upgrade=${plan}&billing=${billing}`, { replace: true });
      else navigate("/app", { replace: true });
    }
  }, [authLoading, user, navigate, params]);

  useEffect(() => {
    const ref = params.get("ref");
    if (ref) setReferralCode(ref.toUpperCase());
  }, [params]);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    // Preserve referral code across OAuth if present
    if (referralCode) sessionStorage.setItem("pending_ref", referralCode);
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) await login(email, password);
      else await register(email, password, name, referralCode || undefined);
      toast.success(isLogin ? t("login_success") : t("register_success"));
      const plan = params.get("plan");
      const billing = params.get("billing");
      if (plan && billing) navigate(`/app/account?upgrade=${plan}&billing=${billing}`);
      else navigate("/app");
    } catch (err) {
      toast.error(errorMessage(err, t));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0E0E12] text-white grid lg:grid-cols-2">
      <div className="hidden lg:block relative overflow-hidden border-r border-white/10">
        <div className="absolute inset-0" style={{
          background: "radial-gradient(circle at 30% 40%, rgba(249,115,22,0.35), transparent 60%), radial-gradient(circle at 70% 70%, rgba(139,92,246,0.15), transparent 50%), #121218"
        }} />
        <div className="relative h-full flex flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
            <img src="/logo-mark.png" alt="Vixelforge" className="w-8 h-8 shrink-0" />
            <span className="font-black text-xl tracking-tight">Vixelforge</span>
          </Link>
          <div>
            <h2 className="font-black text-4xl leading-tight tracking-tight max-w-md mb-4">
              {t("tagline_1")}<br/>
              <span className="text-orange-400">{t("tagline_2")}</span> {t("tagline_3")}
            </h2>
            <p className="text-zinc-400 max-w-md">{t("hero_sub")}</p>
          </div>
          <div className="text-xs text-zinc-500">© 2026 Vixelforge</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="flex justify-end mb-6"><LanguageSwitcher /></div>
          <div className="mb-8">
            <h1 className="font-black text-3xl md:text-4xl tracking-tight mb-2">
              {isLogin ? t("signin") : t("signup_title")}
            </h1>
            <p className="text-zinc-400 text-sm">{isLogin ? t("welcome_back") : t("signup_hint")}</p>
          </div>

          <Button
            onClick={handleGoogle}
            variant="outline"
            className="w-full h-11 rounded-full bg-white text-black hover:bg-zinc-100 border-white/10 font-semibold"
            data-testid="google-auth-btn"
          >
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            {t("continue_google")}
          </Button>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs text-zinc-500 uppercase tracking-widest">{t("or")}</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <Label htmlFor="name" className="text-xs uppercase tracking-widest text-zinc-400 font-bold">{t("name")}</Label>
                <Input id="name" data-testid="name-input" required value={name} onChange={(e) => setName(e.target.value)} className="bg-[#16161D] border-white/10 focus:ring-orange-500 mt-1.5 h-11" />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-zinc-400 font-bold">{t("email")}</Label>
              <Input id="email" type="email" data-testid="email-input" required value={email} onChange={(e) => setEmail(e.target.value)} className="bg-[#16161D] border-white/10 focus:ring-orange-500 mt-1.5 h-11" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-widest text-zinc-400 font-bold">{t("password")}</Label>
              <Input id="password" type="password" data-testid="password-input" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="bg-[#16161D] border-white/10 focus:ring-orange-500 mt-1.5 h-11" />
            </div>
            {!isLogin && (
              <div>
                <Label htmlFor="ref" className="text-xs uppercase tracking-widest text-zinc-400 font-bold">{t("referral_code_label")}</Label>
                <Input id="ref" data-testid="referral-input" value={referralCode} onChange={(e) => setReferralCode(e.target.value.toUpperCase())} placeholder={t("referral_code_ph")} className="bg-[#16161D] border-white/10 focus:ring-orange-500 mt-1.5 h-11 font-mono uppercase tracking-widest" />
              </div>
            )}
            <Button type="submit" disabled={loading} data-testid="auth-submit-btn" className="w-full h-11 rounded-full bg-orange-500 hover:bg-orange-600 text-black font-bold">
              {loading ? "..." : (isLogin ? t("login_btn") : t("register_btn"))}
            </Button>
          </form>

          {isLogin && (
            <p className="text-sm text-center mt-4">
              <Link to="/forgot-password" className="text-zinc-400 hover:text-orange-300" data-testid="forgot-password-link">
                {t("forgot_password") || "Forgot password?"}
              </Link>
            </p>
          )}

          <p className="text-sm text-zinc-500 mt-6 text-center">
            {isLogin ? t("no_account") + " " : t("have_account") + " "}
            <Link to={isLogin ? "/signup" : "/login"} className="text-orange-400 hover:text-orange-300" data-testid="auth-switch-link">
              {isLogin ? t("register_btn") : t("login_btn")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
